import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Button } from '@/design-system/button';
import { Input, InputRoot } from '@/design-system/input';
import { Popover, PopoverTrigger } from '@/design-system/popover';
import { FilterPopoverContent } from '@/components/FilterPopover';
import { SearchSelect } from '@/design-system/search-select';
import { useSearchableOptions } from '@/hooks/useSearchableOptions';
import { searchHubOptions } from '@/services/api/pitstops';
import { shipmentsAPI, ShipmentExim } from '@/services/api/shipments';

import { useProvidersDropdown } from './useProvidersDropdown';

export type DatePreset = 'today' | 'yesterday' | 'last_7_days' | 'last_30_days';

export interface ShipmentFilterValue {
  /** Numeric hub id stringified. */
  hubId: string;
  /** Numeric provider id stringified (matches the dropdown select API). */
  clientId: string;
  /** Upload batch code (e.g. UPLGKI260608A3K9X). */
  uploadShipmentCode: string;
  /** Preset chip. Mutually exclusive with the manual `createdFrom/To` pair —
   *  picking a preset clears the manual range and vice versa. */
  datePreset: DatePreset | '';
  /** Manual date inputs (`YYYY-MM-DD`). Sent to BE as `createdFrom`/`createdTo`. */
  createdFrom: string;
  createdTo: string;
  /** EXIM direction. Empty = all directions. */
  exim: ShipmentExim | '';
  /** Driver code or name, partial match (server-side). */
  driver: string;
}

export const EMPTY_FILTERS: ShipmentFilterValue = {
  hubId: '',
  clientId: '',
  uploadShipmentCode: '',
  datePreset: '',
  createdFrom: '',
  createdTo: '',
  exim: '',
  driver: '',
};

/**
 * Single source of truth for the "Filter (N)" badge counter. Date counts as
 * ONE active filter whether it's set via preset or manual range.
 */
export const countActiveFilters = (v: ShipmentFilterValue): number =>
  (v.hubId ? 1 : 0) +
  (v.clientId ? 1 : 0) +
  (v.uploadShipmentCode ? 1 : 0) +
  (v.datePreset || v.createdFrom || v.createdTo ? 1 : 0) +
  (v.exim ? 1 : 0) +
  (v.driver.trim() ? 1 : 0);

const DATE_PRESETS: { key: DatePreset; label: string }[] = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'last_7_days', label: 'Last 7 days' },
  { key: 'last_30_days', label: 'Last 30 days' },
];

const DIRECTION_FILTERS: { key: ShipmentExim; label: string }[] = [
  { key: 'EXPORT', label: 'Export' },
  { key: 'IMPORT', label: 'Import' },
  { key: 'GENERAL', label: 'Umum' },
];

interface ShipmentFilterPanelProps {
  open: boolean;
  /** Controls the Radix popover. Opening seeds the draft in the parent. */
  onOpenChange: (open: boolean) => void;
  /** Number of applied filters — shown as an ink pill on the trigger. */
  activeFilterCount: number;
  value: ShipmentFilterValue;
  onChange: (next: ShipmentFilterValue) => void;
  onApply: () => void;
  onReset: () => void;
}

const ShipmentFilterPanel: React.FC<ShipmentFilterPanelProps> = ({
  open,
  onOpenChange,
  activeFilterCount,
  value,
  onChange,
  onApply,
  onReset,
}) => {
  const labelCls = 'mb-2 block text-[11px] font-medium text-text-sub-600';

  // ── Hub server-side search ────────────────────────────────────────────────
  // Fetch on open, `?search=`-debounced. Replaces the prefetched static list.
  const hubs = useSearchableOptions(open, searchHubOptions);

  // ── Client (provider) server-side search ─────────────────────────────────
  // Same hook the import modal uses: page-1 fetch on open, 300ms-debounced
  // `?search=`, lazy `loadMore()`, plus an id→name cache so the picked client
  // keeps its label after a narrowing search drops it from the results.
  const providers = useProvidersDropdown(open);

  // ── Batch code async search ──────────────────────────────────────────────
  const [batchOptions, setBatchOptions] = useState<{ value: string; label: string }[]>([]);
  const [batchLoading, setBatchLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchBatches = useCallback((search: string) => {
    setBatchLoading(true);
    shipmentsAPI
      .listUploadShipments({ search: search || undefined, limit: 100, page: 1 })
      .then((res) => {
        const opts = (res.data ?? [])
          .filter((u) => u.code)
          .map((u) => ({ value: u.code!, label: u.code! }));
        setBatchOptions(opts);
      })
      .catch(() => setBatchOptions([]))
      .finally(() => setBatchLoading(false));
  }, []);

  // Load initial options when the popover opens.
  useEffect(() => {
    if (open) fetchBatches('');
  }, [open, fetchBatches]);

  const handleBatchSearch = (search: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchBatches(search), 350);
  };

  // Mirror invoice's toggle behavior: clicking an active preset clears it.
  const updateDatePreset = (preset: DatePreset) => {
    onChange({
      ...value,
      datePreset: value.datePreset === preset ? '' : preset,
      createdFrom: '',
      createdTo: '',
    });
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button tone="neutral" style="stroke" size="sm">
          <i className="ri-equalizer-2-line mr-1.5 text-[14px]" />
          Filter
          {activeFilterCount > 0 && (
            <span className="ml-1.5 rounded-md bg-bg-strong-950 px-1.5 py-0.5 text-[10px] font-bold tabular-nums text-text-white-0">
              {activeFilterCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <FilterPopoverContent
        align="end"
        className="w-[360px] p-0"
        aria-label="Filter shipments"
      >
        <div className="flex items-center justify-between border-b border-stroke-soft-200 px-4 py-3">
          <p className="text-sm font-semibold tracking-tight text-text-strong-950">
            Filter shipments
          </p>
          <button
            type="button"
            onClick={onReset}
            className="text-[11px] font-medium text-text-sub-600 transition-colors hover:text-accent"
          >
            Reset
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto space-y-4 px-4 py-3">
          <div>
            <p className={labelCls}>Date</p>
            <div className="flex flex-wrap gap-1">
              {DATE_PRESETS.map((p) => {
                const active = value.datePreset === p.key;
                return (
                  <button
                    key={p.key}
                    type="button"
                    onClick={() => updateDatePreset(p.key)}
                    className={`rounded-lg border px-2.5 py-1 text-xs font-semibold tracking-tight transition-colors ${
                      active
                        ? 'border-accent text-accent'
                        : 'border-stroke-soft-200 text-text-sub-600 hover:bg-bg-weak-50'
                    }`}
                  >
                    {p.label}
                  </button>
                );
              })}
            </div>
            <div className="mt-2 flex items-center gap-2">
              <InputRoot size="sm" className="flex-1">
                <Input
                  type="date"
                  value={value.createdFrom}
                  onChange={(e) =>
                    onChange({
                      ...value,
                      createdFrom: e.target.value,
                      datePreset: '',
                    })
                  }
                />
              </InputRoot>
              <span className="text-xs text-text-soft-400">to</span>
              <InputRoot size="sm" className="flex-1">
                <Input
                  type="date"
                  value={value.createdTo}
                  onChange={(e) =>
                    onChange({
                      ...value,
                      createdTo: e.target.value,
                      datePreset: '',
                    })
                  }
                />
              </InputRoot>
            </div>
          </div>

          <div>
            <p className={labelCls}>Type</p>
            <div className="flex flex-wrap gap-1">
              {DIRECTION_FILTERS.map((d) => {
                const active = value.exim === d.key;
                return (
                  <button
                    key={d.key}
                    type="button"
                    onClick={() =>
                      onChange({
                        ...value,
                        exim: active ? '' : d.key,
                      })
                    }
                    className={`rounded-lg border px-2.5 py-1 text-xs font-semibold tracking-tight transition-colors ${
                      active
                        ? 'border-accent text-accent'
                        : 'border-stroke-soft-200 text-text-sub-600 hover:bg-bg-weak-50'
                    }`}
                  >
                    {d.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <p className={labelCls}>Driver</p>
            <InputRoot size="sm">
              <Input
                value={value.driver}
                onChange={(e) => onChange({ ...value, driver: e.target.value })}
                placeholder="Kode atau nama driver…"
                aria-label="Driver"
              />
            </InputRoot>
          </div>

          <div>
            <p className={labelCls}>Batch code</p>
            <SearchSelect
              options={batchOptions}
              value={value.uploadShipmentCode || undefined}
              onChange={(v) =>
                onChange({ ...value, uploadShipmentCode: v as string })
              }
              placeholder="Search batch code…"
              disableLocalFilter
              loading={batchLoading}
              onSearchChange={handleBatchSearch}
              allowClear
              emptyMessage="No batches found"
            />
          </div>

          <div>
            <p className={labelCls}>Hub</p>
            <SearchSelect
              options={hubs.options.map((h) => ({
                value: h.id,
                label: h.name,
              }))}
              value={value.hubId || undefined}
              selectedLabelFallback={
                value.hubId ? hubs.labelFor(value.hubId) : undefined
              }
              onChange={(v) =>
                onChange({ ...value, hubId: (v as string | undefined) ?? '' })
              }
              onSearchChange={hubs.setSearch}
              disableLocalFilter
              loading={hubs.loading}
              placeholder="All hubs"
              allowClear
              emptyMessage={hubs.error ?? 'No matching hub'}
            />
          </div>

          <div>
            <p className={labelCls}>Client</p>
            <SearchSelect
              options={providers.options.map((p) => ({
                value: String(p.id),
                label: p.name,
              }))}
              value={value.clientId || undefined}
              selectedLabelFallback={
                value.clientId ? providers.labelFor(value.clientId) : undefined
              }
              onChange={(v) =>
                onChange({ ...value, clientId: (v as string | undefined) ?? '' })
              }
              onSearchChange={providers.setSearch}
              disableLocalFilter
              loading={providers.loading}
              hasMore={providers.hasMore}
              onLoadMore={providers.loadMore}
              placeholder="All clients"
              allowClear
              emptyMessage={providers.error ?? 'No matching client'}
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-stroke-soft-200 px-4 py-3">
          <Button
            tone="neutral"
            style="stroke"
            size="sm"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button size="sm" onClick={onApply}>
            Apply
          </Button>
        </div>
      </FilterPopoverContent>
    </Popover>
  );
};

/**
 * Convert a preset chip → ISO `YYYY-MM-DD` range using **local TZ** (which
 * matches what axios sends as `X-Client-Time-Zone`). Returns null fields when
 * no preset is set — caller falls back to manual `createdFrom/To`.
 *
 * Kept here (not in the page) so the preset → range contract lives next to
 * the preset definitions; less drift if presets are renamed/added.
 */
export function resolvePresetRange(
  preset: DatePreset | ''
): { from: string; to: string } | null {
  if (!preset) return null;
  const now = new Date();
  const today = startOfLocalDay(now);
  let from = today;
  let to = today;
  if (preset === 'yesterday') {
    from = addDays(today, -1);
    to = addDays(today, -1);
  } else if (preset === 'last_7_days') {
    from = addDays(today, -6);
  } else if (preset === 'last_30_days') {
    from = addDays(today, -29);
  }
  return { from: toIsoDate(from), to: toIsoDate(to) };
}

function startOfLocalDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function addDays(d: Date, n: number): Date {
  const out = new Date(d);
  out.setDate(out.getDate() + n);
  return out;
}

/** `YYYY-MM-DD` in local TZ — avoids `.toISOString()`'s UTC drift. */
function toIsoDate(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

export default ShipmentFilterPanel;
